import { useSyncExternalStore } from 'react'
import type { PresenceStatus } from '../types/chat'
import { derivePresence, normalizePresence, PRESENCE_ACTIVE_GRACE_MS } from '../utils/presence'
import { getProviderActivityStatus } from '../features/provider-activity/activity-status'

type PresenceRecord = {
  status: PresenceStatus
  /** Epoch ms of the last activity heartbeat we know about (null = unknown / signed out). */
  lastActiveAt: number | null
}

/**
 * A user's CUSTOM activity status reduced to what an avatar needs to render it:
 * the emoji glyph (shown in the avatar corner instead of the presence dot) and
 * the words (used for the accessible label). Resolved from the user's
 * `settingsJson.activityStatus`.
 *
 * `expiresAt` (ISO-8601) lets the store auto-clear a timed status live: reads
 * treat an expired glyph as "no status" and the periodic decay tick re-emits so
 * the self avatar reverts to plain presence without waiting for a re-poll.
 */
export type ActivityGlyph = { emoji: string; label: string; expiresAt?: string | null }

const presenceByUserId = new Map<string, PresenceRecord>()
/**
 * Custom activity status per user, keyed by userId. A present key (even with a
 * `null` value) means we have an AUTHORITATIVE answer for that user (seeded from
 * the directory / their own save) and should NOT fall back to a possibly-stale
 * `settingsJson` carried on a local user object. An absent key means "unknown".
 */
const activityByUserId = new Map<string, ActivityGlyph | null>()
/**
 * People whose presence the server won't show this viewer (no shared
 * conversation or care team): their status is unknown — never "offline".
 */
const hiddenPresenceUserIds = new Set<string>()
const listeners = new Set<() => void>()
let version = 0
let decayTimer: ReturnType<typeof setInterval> | null = null

/** Re-derive statuses periodically so ACTIVE decays to IDLE/OFFLINE live. */
const DECAY_TICK_MS = 30_000

function emit() {
  version += 1
  for (const listener of listeners) listener()
}

function parseTimestamp(value: string | number | null | undefined): number | null {
  if (value == null) return null
  const ts = typeof value === 'number' ? value : Date.parse(value)
  return Number.isFinite(ts) ? ts : null
}

/**
 * Merge an incoming presence write without letting stale OFFLINE or older
 * lastActiveAt values from another tab/device overwrite fresher activity.
 */
function mergePresenceRecord(
  prev: PresenceRecord | undefined,
  status: PresenceStatus,
  lastActiveAtInput: string | number | null | undefined,
): PresenceRecord {
  const incomingStatus = normalizePresence(status)

  let nextLastActiveAt = prev?.lastActiveAt ?? null
  if (lastActiveAtInput === null) {
    nextLastActiveAt = null
  } else if (lastActiveAtInput !== undefined) {
    const incomingTs = parseTimestamp(lastActiveAtInput)
    if (incomingTs != null) {
      if (nextLastActiveAt == null || incomingTs >= nextLastActiveAt) {
        nextLastActiveAt = incomingTs
      }
    }
  }

  let nextStatus = incomingStatus
  if (incomingStatus === 'OFFLINE' && nextLastActiveAt != null) {
    const age = Date.now() - nextLastActiveAt
    if (age <= PRESENCE_ACTIVE_GRACE_MS * 6) {
      nextStatus = prev?.status === 'IDLE' ? 'IDLE' : 'ACTIVE'
    }
  }

  if (nextLastActiveAt != null && incomingStatus === 'ACTIVE') {
    nextStatus = 'ACTIVE'
  }

  return { status: nextStatus, lastActiveAt: nextLastActiveAt }
}

function glyphFromSettings(user: { settingsJson?: unknown }): ActivityGlyph | null {
  // getProviderActivityStatus already filters out an expired status; we still
  // carry expiresAt forward so a not-yet-expired glyph can be aged out live by
  // the decay tick without waiting for the next directory poll.
  const status = getProviderActivityStatus(user)
  if (!status) return null
  return { emoji: status.emoji, label: status.label, expiresAt: status.expiresAt ?? null }
}

function isGlyphExpired(glyph: ActivityGlyph | null, now: number = Date.now()): boolean {
  if (!glyph?.expiresAt) return false
  const expiresAtMs = Date.parse(glyph.expiresAt)
  return !Number.isNaN(expiresAtMs) && expiresAtMs <= now
}

function sameGlyph(a: ActivityGlyph | null, b: ActivityGlyph | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.emoji === b.emoji && a.label === b.label && (a.expiresAt ?? null) === (b.expiresAt ?? null)
}

/**
 * Record the AUTHORITATIVE activity glyph for a user (from the directory or the
 * user's own save). Returns whether the stored value changed. Always sets the
 * key — even to `null` — so reads treat the user as "known" (no resurrecting a
 * stale `settingsJson` on a local object).
 */
function applyActivity(userId: string, glyph: ActivityGlyph | null): boolean {
  const had = activityByUserId.has(userId)
  const prev = had ? activityByUserId.get(userId)! : null
  activityByUserId.set(userId, glyph)
  return !had || !sameGlyph(prev, glyph)
}

export function seedPresenceBulk(
  users: ReadonlyArray<{
    userId: string
    presenceStatus?: PresenceStatus | null
    lastActiveAt?: string | number | null
    presenceVisible?: boolean | null
    settingsJson?: unknown
  }>,
): void {
  let changed = false
  for (const user of users) {
    if (user.presenceVisible === null) {
      // The server couldn't check just now: keep what we know, unknown otherwise.
      if (!presenceByUserId.has(user.userId) && !hiddenPresenceUserIds.has(user.userId)) {
        hiddenPresenceUserIds.add(user.userId)
        changed = true
      }
      if ('settingsJson' in user && applyActivity(user.userId, glyphFromSettings(user))) changed = true
      continue
    }
    if (user.presenceVisible === false) {
      if (!hiddenPresenceUserIds.has(user.userId)) {
        hiddenPresenceUserIds.add(user.userId)
        changed = true
      }
    } else if (user.presenceVisible === true && hiddenPresenceUserIds.delete(user.userId)) {
      changed = true
    }
    const prev = presenceByUserId.get(user.userId)
    const merged = mergePresenceRecord(
      prev,
      normalizePresence(user.presenceStatus),
      user.lastActiveAt,
    )
    if (!prev || prev.status !== merged.status || prev.lastActiveAt !== merged.lastActiveAt) {
      presenceByUserId.set(user.userId, merged)
      changed = true
    }
    if ('settingsJson' in user) {
      if (applyActivity(user.userId, glyphFromSettings(user))) changed = true
    }
  }
  if (changed) emit()
}

/**
 * Set (or clear with `null`) the current user's custom activity status so their
 * own avatar reflects it instantly across every surface — not just the footer —
 * without waiting for the next directory poll.
 */
export function setUserActivityStatus(userId: string, glyph: ActivityGlyph | null): void {
  if (applyActivity(userId, glyph)) emit()
}

export function setUserPresence(
  userId: string,
  status: PresenceStatus,
  lastActiveAt?: string | number | null,
): void {
  // A live update means the server let us subscribe: the status is known.
  if (hiddenPresenceUserIds.delete(userId)) emit()
  const prev = presenceByUserId.get(userId)
  const merged = mergePresenceRecord(prev, status, lastActiveAt)
  if (prev && prev.status === merged.status && prev.lastActiveAt === merged.lastActiveAt) return
  presenceByUserId.set(userId, merged)
  emit()
}

/** Forget every user's presence, activity glyph and visibility (sign-out: the next account starts clean). */
export function resetPresenceStore(): void {
  presenceByUserId.clear()
  activityByUserId.clear()
  hiddenPresenceUserIds.clear()
  emit()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  if (!decayTimer) {
    decayTimer = setInterval(() => emit(), DECAY_TICK_MS)
  }
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && decayTimer) {
      clearInterval(decayTimer)
      decayTimer = null
    }
  }
}

function getSnapshot(): number {
  return version
}

function usePresenceVersion(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function resolveUserPresence(
  user:
    | {
        userId: string
        presenceStatus?: PresenceStatus | null
        lastActiveAt?: string | number | null
      }
    | undefined
    | null,
): PresenceStatus {
  if (!user) return 'OFFLINE'
  const record = presenceByUserId.get(user.userId)
  if (record) return derivePresence(record.status, record.lastActiveAt)
  return derivePresence(user.presenceStatus, user.lastActiveAt ?? null)
}

export function resolveUserLastActiveAt(
  user:
    | { userId: string; lastActiveAt?: string | number | null }
    | undefined
    | null,
): number | null {
  if (!user) return null
  return (
    presenceByUserId.get(user.userId)?.lastActiveAt ??
    parseTimestamp(user.lastActiveAt)
  )
}

export function useResolvedPresence(
  user:
    | {
        userId: string
        presenceStatus?: PresenceStatus | null
        lastActiveAt?: string | number | null
      }
    | undefined
    | null,
): PresenceStatus {
  usePresenceVersion()
  return resolveUserPresence(user)
}

/** True when the server doesn't let this viewer see the user's presence (show nothing, not "Offline"). */
export function isPresenceHidden(userId: string | null | undefined): boolean {
  return Boolean(userId) && hiddenPresenceUserIds.has(userId!)
}

export function usePresenceHidden(userId: string | null | undefined): boolean {
  usePresenceVersion()
  return isPresenceHidden(userId)
}

export function useResolvedLastActiveAt(
  user:
    | { userId: string; lastActiveAt?: string | number | null }
    | undefined
    | null,
): number | null {
  usePresenceVersion()
  return resolveUserLastActiveAt(user)
}

/**
 * Resolve a user's CUSTOM activity status (e.g. 🤒 Unwell). Prefers the
 * authoritative store value (seeded from the directory / the user's own save);
 * otherwise falls back to the `settingsJson` carried on the passed object so a
 * freshly-loaded user still resolves before the next presence sync.
 */
export function resolveUserActivityStatus(
  user: { userId: string; settingsJson?: unknown } | undefined | null,
): ActivityGlyph | null {
  if (!user) return null
  if (activityByUserId.has(user.userId)) {
    const stored = activityByUserId.get(user.userId) ?? null
    return isGlyphExpired(stored) ? null : stored
  }
  const glyph = glyphFromSettings(user)
  return isGlyphExpired(glyph) ? null : glyph
}

export function useResolvedActivityStatus(
  user: { userId: string; settingsJson?: unknown } | undefined | null,
): ActivityGlyph | null {
  usePresenceVersion()
  return resolveUserActivityStatus(user)
}

/**
 * The single way avatars decide what to show: a custom activity status (rendered
 * as its emoji glyph, replacing the presence dot per #95) when set, otherwise the
 * live presence (active/idle dot; offline shows nothing). `statusEmoji` is passed
 * to {@link AvatarImage}; `statusLabel` is the words for an accessible label.
 */
export function useDisplayStatus(
  user:
    | {
        userId: string
        presenceStatus?: PresenceStatus | null
        lastActiveAt?: string | number | null
        settingsJson?: unknown
      }
    | undefined
    | null,
): { presence: PresenceStatus; statusEmoji: string | null; statusLabel: string | null } {
  usePresenceVersion()
  const presence = resolveUserPresence(user)
  const activity = resolveUserActivityStatus(user)
  return {
    presence,
    statusEmoji: activity?.emoji ?? null,
    statusLabel: activity?.label ?? null,
  }
}
